import {AppStore} from "../stores/app.store";
import {useAppStore} from "../common/app.store.consumer";
import * as React from "react";
import {ValidatableForm, ValidatableInput, ValidationMessage} from "../common/validation";
import {RouteComponentProps, withRouter} from "react-router";
import * as Yup from "yup";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IconName} from "@fortawesome/pro-regular-svg-icons";
import logoCih from "../assets/img/logo.png";

interface LoginInnerState {
	isSuccessful: boolean;
	isSuccessfulVisible: boolean;
	hasLoginAttempted: boolean;
	model: {
		email?: string;
	};
	hideInitialState: boolean;
	pendingAnswerSheetId?: string;
}

interface LoginProps {
	appStore?: AppStore;
	initialState: LoginState;
}

export enum LoginState {
	Default,
	SessionExpired,
	AccessLinkExpired,
	ForcedLogout,
	AccessLinkNotFound,
	AccessDenied,
}

type LoginPropsWithRouting = LoginProps & RouteComponentProps<{redirectUrl?: string}>;

const emailModel = Yup.object({
	email: Yup.string().required("Email field is required.").email("Please enter a valid email address."),
});
const unsuccessfulLoginAttemptMessage = "The login request failed";

@useAppStore()
export default class LoginWithoutRouter extends React.Component<LoginPropsWithRouting, LoginInnerState> {
	constructor(props: LoginPropsWithRouting) {
		super(props);
		this.state = {
			isSuccessful: false,
			isSuccessfulVisible: false,
			hasLoginAttempted: false,
			model: {
				email: "",
			},
			hideInitialState: false,
			pendingAnswerSheetId: null,
		};
	}

	componentDidMount() {
		this.props.appStore.userStore.isLoginVisible = true;

		// get pendingAnswerSheetId from session storage
		const pendingAnswerSheetId = window.sessionStorage.getItem("pendingAnswerSheetId");
		if (pendingAnswerSheetId) {
			this.setState({pendingAnswerSheetId: pendingAnswerSheetId});
		}
	}

	componentWillUnmount() {
		this.props.appStore.userStore.isLoginVisible = false;
	}

	private onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		this.setState({
			isSuccessfulVisible: false,
		});
		let userStore = this.props.appStore.userStore;

		let res = await userStore.loginByEmail(this.state.model.email, this.getRedirectUrl(), this.state.pendingAnswerSheetId);
		this.setState({
			isSuccessful: res,
			isSuccessfulVisible: true,
			hasLoginAttempted: true,
		});
		window.scrollTo(0, 0);
	};

	private onEmailChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({
			model: {
				email: e.target.value,
			},
		});
	};

	private onEmailFocused = () => {
		this.setState({
			isSuccessfulVisible: false,
			hideInitialState: true,
		});
	};

	private getRedirectUrl(): string {
		if (this.props.match.params.redirectUrl) {
			return decodeURIComponent(this.props.match.params.redirectUrl);
		}

		return "/#/";
	}

	private onClickLoginWithSSO = () => {
		this.props.appStore.userStore.sendToOpenIdLogin(this.getRedirectUrl(), this.state.pendingAnswerSheetId);
	};

	render() {
		if (!this.props.appStore.configuration.isLoginPageEnabled) {
			this.props.appStore.userStore.sendToOpenIdLogin(this.getRedirectUrl(), this.state.pendingAnswerSheetId);
			return null;
		}

		let loginSuccessfulMsg = this.state.isSuccessful && this.state.isSuccessfulVisible && <span>Your login was successful, check your email inbox.</span>;
		let ico: IconName = this.state.isSuccessful ? "check-circle" : "times-circle";
		return (
			<div className="container cih-login-page cih-flex cih-justify-center">
				<div className="cih-login">
					<div className="cih-login-img">
						<div className="cih-logo">
							<img src={logoCih} alt="CIH Logo" />
						</div>
					</div>
					<div className="login-box">
						<ValidatableForm onSubmit={(e: React.FormEvent<HTMLFormElement>) => this.onSubmit(e)} model={this.state.model} validateOnBlur={false} schema={emailModel}>
							{(validationResult) => (
								<div>
									<h2 className="cih-h2">Welcome to the Chartered Institute of Housing</h2>
									<p>If your email is registered with us, you will receive a one-time-use access link to enter the platform. Please make sure to check your mailbox.</p>
									<form className="cih-flex cih-flex-col">
										<div>
											<ValidatableInput model={this.state.model} fieldName="email" onChanged={(e: React.ChangeEvent<HTMLInputElement>) => this.onEmailChanged(e)}>
												<input type="text" id="email" name="email" placeholder="Enter your email" onFocus={() => this.onEmailFocused()} />
											</ValidatableInput>
										</div>
										{this.state.hasLoginAttempted && (
											<div className={"cih-notification small " + (this.state.isSuccessful ? "success" : "warning")}>
												<FontAwesomeIcon transform="grow-2.5" icon={["fas", ico]} />
												<span>
													<ValidationMessage fieldName="email" validationResult={validationResult} />
												</span>
												<span>{this.state.isSuccessful ? loginSuccessfulMsg : unsuccessfulLoginAttemptMessage}</span>
											</div>
										)}
										<div className="cih-flex cih-login-btn-group">
											<button className="cih-btn-send arrowbutton __transparent">
												<span className="buttontext">Send</span>
												<span className="buttonicon">
													<span className="buttonicon-topline"></span>
													<span className="buttonicon-middleline"></span>
													<span className="buttonicon-bottomline"></span>
												</span>
											</button>
											{this.props.appStore.configuration.isOpenIdLoginEnabled && (
												<button className="cih-btn-sso" onClick={this.onClickLoginWithSSO}>
													Click here to Login with SSO
												</button>
											)}
										</div>
									</form>
								</div>
							)}
						</ValidatableForm>
					</div>
				</div>
			</div>
		);
	}
}

export const Login = withRouter(LoginWithoutRouter);
